import React, { useEffect, useState } from "react";
function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

import { api } from "@/lib/api";
import { inr, dateStr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import { Plus, Wallet, Target, TrendingUp, Activity, AlertCircle, ScanLine, Loader2, Image as ImageIcon, Layers, X, Check, ListTree } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import WBSPanel from "@/pages/WBSPanel";

function BillLink({ path }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (url) return window.open(url, "_blank");
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/files/${path}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error("Not found");
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      setUrl(u);
      window.open(u, "_blank");
    } catch { toast.error("Bill image not available"); }
    finally { setLoading(false); }
  };
  return (
    <button data-testid={`bill-view-${path}`} onClick={load} disabled={loading}
      className="text-xs text-primary hover:underline inline-flex items-center gap-1">
      <ImageIcon className="h-3 w-3" /> {loading ? "…" : "View"}
    </button>
  );
}

function WBSInlineTag({ expense, wbsList, onChanged }) {
  const [val, setVal] = useState(expense.wbs_id || "");
  useEffect(() => { setVal(expense.wbs_id || ""); }, [expense.wbs_id]);
  const change = async (v) => {
    const wbs_id = v === "__none" ? "" : v;
    setVal(wbs_id);
    try {
      // Re-submit the expense with new wbs_id (backend PATCH not available; use POST endpoint via update via mongo passthrough)
      const r = await fetch(`${API}/expenses/${expense.expense_id}/wbs`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("session_token") || ""}`,
        },
        body: JSON.stringify({ wbs_id }),
      });
      if (!r.ok) throw new Error();
      toast.success("Milestone updated");
      onChanged?.();
    } catch { toast.error("Failed"); setVal(expense.wbs_id || ""); }
  };
  const label = wbsList.find((w) => w.wbs_id === val);
  return (
    <Select value={val || "__none"} onValueChange={change}>
      <SelectTrigger data-testid={`exp-wbs-inline-${expense.expense_id}`} className="h-7 text-[11px] w-[140px]">
        {label ? <span className="truncate">{label.code} · {label.name}</span> : <span className="text-muted-foreground">— untagged —</span>}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">— untagged —</SelectItem>
        {wbsList.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const CATEGORIES = ["Aluminium Profile (YKK)", "Glass", "Hardware & Gaskets", "Sealants & Adhesives", "Fabrication Labor", "Site Installation", "Cladding Material", "Overhead"];

export default function Finance() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [budget, setBudget] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [evm, setEvm] = useState(null);
  const [wbsList, setWbsList] = useState([]);
  const [showExpense, setShowExpense] = useState(false);
  const [showBudget, setShowBudget] = useState(false);
  const [showProject, setShowProject] = useState(false);
  const [expenseSearch, setExpenseSearch] = useState("");

  const loadProjects = async () => {
    const r = await api.get("/projects");
    setProjects(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };

  const loadProjectData = async (pid) => {
    const [b, e, v, w] = await Promise.all([
      api.get(`/budget/${pid}`),
      api.get(`/expenses?project_id=${pid}`),
      api.get(`/finance/evm/${pid}`),
      api.get(`/wbs?project_id=${pid}`),
    ]);
    setBudget(b.data); setExpenses(e.data); setEvm(v.data); setWbsList(w.data);
  };

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => { if (selected) loadProjectData(selected.project_id); }, [selected]);

  const budgetByCat = CATEGORIES.map((c) => {
    const rows = budget.filter((b) => b.category === c);
    const planned = rows.reduce((s, r) => s + r.planned_amount, 0);
    const spent = expenses.filter((e) => e.category === c).reduce((s, r) => s + r.amount, 0);
    return { category: c, Planned: planned, Spent: spent };
  });

  return (
    <div className="space-y-6" data-testid="finance-root">
      <SectionHeader
        title="Finance & Accounting"
        subtitle="Project budgets, expenses, GST/TDS and Earned Value Management"
        right={
          <div className="flex gap-2">
            <Select value={selected?.project_id || ""} onValueChange={(v) => setSelected(projects.find((p) => p.project_id === v))}>
              <SelectTrigger data-testid="project-select" className="w-64 h-9"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    <span className="font-mono text-xs mr-2 text-muted-foreground">{p.code}</span>{p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <NewProjectDialog open={showProject} onOpenChange={setShowProject} onCreated={loadProjects} />
          </div>
        }
      />

      {selected && evm && (
        <>
          {/* EVM KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI testId="evm-pv" label="Planned Value (PV)" value={evm.pv} compact icon={Target} />
            <KPI testId="evm-ev" label="Earned Value (EV)" value={evm.ev} compact icon={TrendingUp} />
            <KPI testId="evm-ac" label="Actual Cost (AC)" value={evm.ac} compact icon={Wallet} />
            <KPI testId="evm-eac" label="Est. at Completion" value={evm.eac} compact icon={Activity} tone={evm.eac > evm.bac ? "bad" : "good"} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI testId="evm-cpi" label="CPI (Cost)" value={evm.cpi.toFixed(2)} suffix="x" tone={evm.cpi >= 1 ? "good" : evm.cpi >= 0.9 ? "warn" : "bad"} />
            <KPI testId="evm-spi" label="SPI (Schedule)" value={evm.spi.toFixed(2)} suffix="x" tone={evm.spi >= 1 ? "good" : evm.spi >= 0.9 ? "warn" : "bad"} />
            <KPI testId="evm-cv" label="Cost Variance (CV)" value={evm.cv} compact tone={evm.cv >= 0 ? "good" : "bad"} />
            <KPI testId="evm-sv" label="Schedule Variance (SV)" value={evm.sv} compact tone={evm.sv >= 0 ? "good" : "bad"} />
          </div>

          <Card className="shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-display flex items-center justify-between">
                {selected.name}
                <Badge variant="outline" className="font-mono text-[10px]">{selected.code}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Client</div>
                  <div className="font-medium mt-0.5">{selected.client}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Location</div>
                  <div className="font-medium mt-0.5">{selected.location || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Manager</div>
                  <div className="font-medium mt-0.5">{selected.manager || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Contract value (BAC)</div>
                  <div className="font-medium mt-0.5">{inr(selected.contract_value, { compact: true })}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Timeline</div>
                  <div className="font-medium mt-0.5 text-xs">{dateStr(selected.start_date)} → {dateStr(selected.end_date)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Progress (Planned / Actual)</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Progress value={selected.actual_progress_pct} className="h-2 flex-1" />
                    <span className="text-xs font-mono">{selected.planned_progress_pct}% / {selected.actual_progress_pct}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="wbs">
            <TabsList>
              <TabsTrigger value="wbs" data-testid="tab-wbs">Milestones · WBS ({wbsList.length})</TabsTrigger>
              <TabsTrigger value="budget" data-testid="tab-budget">Budget vs Spent</TabsTrigger>
              <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses ({expenses.length})</TabsTrigger>
              <TabsTrigger value="evm-chart" data-testid="tab-evm">EVM analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="wbs" className="mt-4">
              <WBSPanel project={selected} onChanged={() => loadProjectData(selected.project_id)} />
            </TabsContent>

            <TabsContent value="budget" className="mt-4">
              <Card className="shadow-none">
                <CardHeader className="pb-3 flex-row items-center justify-between">
                  <CardTitle className="text-sm font-semibold font-display">Budget lines</CardTitle>
                  <NewBudgetDialog
                    open={showBudget} onOpenChange={setShowBudget}
                    projectId={selected.project_id}
                    onCreated={() => loadProjectData(selected.project_id)}
                  />
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-56 mb-4">
                    <ResponsiveContainer>
                      <BarChart data={budgetByCat} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => inr(v, { compact: true, symbol: false })} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => inr(v, { compact: true })} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Planned" fill="#D4A373" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="Spent" fill="#1A3636" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                          <th className="py-2 font-medium">Category</th>
                          <th className="py-2 font-medium">Description</th>
                          <th className="py-2 font-medium text-right">Planned</th>
                          <th className="py-2 font-medium text-right">Committed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budget.map((b) => (
                          <tr key={b.budget_id} className="border-b hover:bg-accent/50">
                            <td className="py-2"><Badge variant="outline">{b.category}</Badge></td>
                            <td className="py-2">{b.description}</td>
                            <td className="py-2 cell-num">{inr(b.planned_amount, { compact: true })}</td>
                            <td className="py-2 cell-num">{inr(b.committed_amount, { compact: true })}</td>
                          </tr>
                        ))}
                        {budget.length === 0 && (
                          <tr><td colSpan={4} className="py-8 text-center text-sm text-muted-foreground">No budget lines yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses" className="mt-4">
              <Card className="shadow-none">
                <CardHeader className="pb-3 flex-row items-center justify-between gap-3">
                  <CardTitle className="text-sm font-semibold font-display shrink-0">Project expenses</CardTitle>
                  <div className="flex gap-2 items-center flex-1 justify-end">
                    <Input
                      value={expenseSearch}
                      onChange={(e) => setExpenseSearch(e.target.value)}
                      placeholder="Search vendor / amount…"
                      data-testid="expense-search"
                      className="h-9 max-w-[240px] text-sm"
                    />
                    <OCRExpenseDialog
                      projectId={selected.project_id} wbsList={wbsList}
                      onCreated={() => loadProjectData(selected.project_id)}
                    />
                    <BulkOCRDialog
                      projectId={selected.project_id} wbsList={wbsList}
                      onCreated={() => loadProjectData(selected.project_id)}
                    />
                    <NewExpenseDialog
                      open={showExpense} onOpenChange={setShowExpense}
                      projectId={selected.project_id} wbsList={wbsList}
                      onCreated={() => loadProjectData(selected.project_id)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                        <th className="py-2 font-medium">Date</th>
                        <th className="py-2 font-medium">Vendor</th>
                        <th className="py-2 font-medium">Description</th>
                        <th className="py-2 font-medium">Category</th>
                        <th className="py-2 font-medium">Milestone</th>
                        <th className="py-2 font-medium text-right">Amount</th>
                        <th className="py-2 font-medium text-right">GST</th>
                        <th className="py-2 font-medium text-right">TDS</th>
                        <th className="py-2 font-medium text-right">Total</th>
                        <th className="py-2 font-medium">Bill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.filter((e) => {
                        const q = expenseSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (e.vendor || "").toLowerCase().includes(q) ||
                          (e.description || "").toLowerCase().includes(q) ||
                          String(e.amount).includes(q) ||
                          String(e.total || "").includes(q) ||
                          (e.invoice_no || "").toLowerCase().includes(q)
                        );
                      }).map((e) => (
                        <tr key={e.expense_id} className="border-b hover:bg-accent/50">
                          <td className="py-2 text-xs">{dateStr(e.date)}</td>
                          <td className="py-2">{e.vendor || "—"}</td>
                          <td className="py-2 max-w-[240px] truncate">{e.description}</td>
                          <td className="py-2"><Badge variant="outline">{e.category}</Badge></td>
                          <td className="py-2">
                            <WBSInlineTag expense={e} wbsList={wbsList}
                              onChanged={() => loadProjectData(selected.project_id)} />
                          </td>
                          <td className="py-2 cell-num">{inr(e.amount, { compact: true })}</td>
                          <td className="py-2 cell-num text-muted-foreground">{inr(e.gst_amount || 0, { compact: true })}</td>
                          <td className="py-2 cell-num text-muted-foreground">-{inr(e.tds_amount || 0, { compact: true })}</td>
                          <td className="py-2 cell-num font-medium">{inr(e.total || 0, { compact: true })}</td>
                          <td className="py-2">
                            {e.bill_image_path ? <BillLink path={e.bill_image_path} /> : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                      {expenses.length === 0 && (
                        <tr><td colSpan={10} className="py-8 text-center text-sm text-muted-foreground">No expenses yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="evm-chart" className="mt-4">
              <Card className="shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold font-display">PV / EV / AC snapshot</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-72">
                    <ResponsiveContainer>
                      <BarChart data={[
                        { name: "PV", value: evm.pv },
                        { name: "EV", value: evm.ev },
                        { name: "AC", value: evm.ac },
                        { name: "EAC", value: evm.eac },
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => inr(v, { compact: true, symbol: false })} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => inr(v, { compact: true })} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                        <Bar dataKey="value" fill="#1A3636" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {evm.cpi < 1 && (
                    <div className="mt-3 text-xs text-destructive flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Cost overrun: CPI {evm.cpi} — project is spending more than earned.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function NewProjectDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "", code: "", client: "", location: "", contract_value: 0,
    start_date: "", end_date: "", manager: "", status: "active",
    planned_progress_pct: 0, actual_progress_pct: 0,
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    try {
      await api.post("/projects", { ...form, contract_value: Number(form.contract_value) });
      toast.success("Project created");
      onOpenChange(false);
      onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="new-project-btn" className="h-9"><Plus className="h-4 w-4 mr-1" />New project</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input data-testid="proj-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Code"><Input data-testid="proj-code" value={form.code} onChange={(e) => set("code", e.target.value)} /></Field>
          <Field label="Client"><Input value={form.client} onChange={(e) => set("client", e.target.value)} /></Field>
          <Field label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} /></Field>
          <Field label="Contract value (₹)"><Input type="number" value={form.contract_value} onChange={(e) => set("contract_value", e.target.value)} /></Field>
          <Field label="Manager"><Input value={form.manager} onChange={(e) => set("manager", e.target.value)} /></Field>
          <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
          <Field label="End date"><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} /></Field>
          <Field label="Planned progress %"><Input type="number" value={form.planned_progress_pct} onChange={(e) => set("planned_progress_pct", Number(e.target.value))} /></Field>
          <Field label="Actual progress %"><Input type="number" value={form.actual_progress_pct} onChange={(e) => set("actual_progress_pct", Number(e.target.value))} /></Field>
        </div>
        <DialogFooter><Button data-testid="proj-save" onClick={save} className="bg-primary hover:bg-primary/90">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewBudgetDialog({ open, onOpenChange, projectId, onCreated }) {
  const [form, setForm] = useState({ category: "Material", description: "", planned_amount: 0, committed_amount: 0 });
  const save = async () => {
    try {
      await api.post("/budget", { project_id: projectId, ...form, planned_amount: Number(form.planned_amount), committed_amount: Number(form.committed_amount) });
      toast.success("Budget line added");
      onOpenChange(false);
      onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="new-budget-btn"><Plus className="h-3 w-3 mr-1" />Add line</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New budget line</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Planned amount"><Input type="number" value={form.planned_amount} onChange={(e) => setForm({...form, planned_amount: e.target.value})} /></Field>
          <Field label="Description" full><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></Field>
          <Field label="Committed amount"><Input type="number" value={form.committed_amount} onChange={(e) => setForm({...form, committed_amount: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewExpenseDialog({ open, onOpenChange, projectId, wbsList = [], onCreated }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10), category: "Material",
    vendor: "", description: "", amount: 0, gst_pct: 18, tds_pct: 0, payment_mode: "bank", invoice_no: "",
    wbs_id: "",
  });
  const save = async () => {
    try {
      await api.post("/expenses", { project_id: projectId, ...form, amount: Number(form.amount), gst_pct: Number(form.gst_pct), tds_pct: Number(form.tds_pct) });
      toast.success("Expense recorded");
      onOpenChange(false);
      onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="new-expense-btn" className="bg-primary hover:bg-primary/90"><Plus className="h-3 w-3 mr-1" />New expense</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Record expense</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Vendor"><Input value={form.vendor} onChange={(e) => setForm({...form, vendor: e.target.value})} /></Field>
          <Field label="Invoice #"><Input value={form.invoice_no} onChange={(e) => setForm({...form, invoice_no: e.target.value})} /></Field>
          <Field label="Milestone / WBS" full>
            <Select value={form.wbs_id || "__none"} onValueChange={(v) => setForm({ ...form, wbs_id: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="expense-wbs"><SelectValue placeholder="— untagged —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— untagged (rolls up at project level only) —</SelectItem>
                {wbsList.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Description" full><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></Field>
          <Field label="Amount (₹)"><Input type="number" data-testid="expense-amount" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></Field>
          <Field label="Payment mode">
            <Select value={form.payment_mode} onValueChange={(v) => setForm({...form, payment_mode: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="rtgs">RTGS/NEFT</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="GST %"><Input type="number" value={form.gst_pct} onChange={(e) => setForm({...form, gst_pct: e.target.value})} /></Field>
          <Field label="TDS %"><Input type="number" value={form.tds_pct} onChange={(e) => setForm({...form, tds_pct: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button data-testid="expense-save" onClick={save} className="bg-primary hover:bg-primary/90">Save expense</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OCRExpenseDialog({ projectId, wbsList = [], onCreated }) {  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [extracted, setExtracted] = useState(null);

  const reset = () => { setPreview(null); setExtracted(null); setBusy(false); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$|^application\/pdf$/i.test(file.type)) {
      toast.error("Upload JPG · PNG · WEBP · PDF");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      const b64 = dataUrl.split(",")[1];
      setBusy(true);
      try {
        const r = await api.post("/expenses/ocr", { image_base64: b64, mime_type: file.type });
        setExtracted({
          date: r.data.date || new Date().toISOString().slice(0, 10),
          vendor: r.data.vendor || "",
          invoice_no: r.data.invoice_no || "",
          description: r.data.description || "",
          amount: Number(r.data.amount || 0),
          gst_pct: Number(r.data.gst_pct || 18),
          tds_pct: Number(r.data.tds_pct || 0),
          category: "Aluminium Profile (YKK)",
          payment_mode: "bank",
          bill_image_path: r.data.bill_image_path || "",
          wbs_id: "",
        });
        toast.success("Fields extracted by AI");
      } catch { toast.error("OCR failed"); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.post("/expenses", { project_id: projectId, ...extracted, amount: Number(extracted.amount) });
      toast.success("Expense saved");
      setOpen(false); reset(); onCreated();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="ocr-expense-btn" className="border-secondary/50">
          <ScanLine className="h-3.5 w-3.5 mr-1 text-secondary" />
          Scan bill
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-secondary" /> Scan vendor bill (AI OCR)
          </DialogTitle>
          <DialogDescription>
            Upload a photo of any vendor bill. Claude Sonnet 4.5 will extract vendor, invoice number, date, amount and taxes for review.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Upload photo · scan · PDF (JPG · PNG · WEBP · PDF)</Label>
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleFile}
                   data-testid="ocr-file" className="block w-full text-xs border rounded-md p-2 file:mr-2 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1" />
            {preview && (
              <div className="mt-3 border rounded p-1 bg-muted">
                {preview.startsWith("data:application/pdf") ? (
                  <div className="p-4 text-center text-xs text-muted-foreground font-mono">
                    📄 PDF received — first page will be scanned by AI.
                  </div>
                ) : (
                  <img src={preview} alt="preview" className="max-h-56 mx-auto object-contain" />
                )}
              </div>
            )}
            {busy && (
              <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Claude Sonnet 4.5 is reading the bill…
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Review & edit extracted fields</Label>
            {!extracted && (
              <div className="text-sm text-muted-foreground border border-dashed rounded-md p-6 text-center">
                Upload a bill photo to see extracted fields here.
              </div>
            )}
            {extracted && (
              <div className="grid grid-cols-2 gap-2">
                <Field label="Vendor" full><Input value={extracted.vendor} onChange={(e) => setExtracted({...extracted, vendor: e.target.value})} /></Field>
                <Field label="Invoice #"><Input value={extracted.invoice_no} onChange={(e) => setExtracted({...extracted, invoice_no: e.target.value})} /></Field>
                <Field label="Date"><Input type="date" value={extracted.date} onChange={(e) => setExtracted({...extracted, date: e.target.value})} /></Field>
                <Field label="Amount (₹)"><Input type="number" value={extracted.amount} onChange={(e) => setExtracted({...extracted, amount: e.target.value})} /></Field>
                <Field label="Category">
                  <Select value={extracted.category} onValueChange={(v) => setExtracted({...extracted, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="GST %"><Input type="number" value={extracted.gst_pct} onChange={(e) => setExtracted({...extracted, gst_pct: e.target.value})} /></Field>
                <Field label="TDS %"><Input type="number" value={extracted.tds_pct} onChange={(e) => setExtracted({...extracted, tds_pct: e.target.value})} /></Field>
                <Field label="Milestone / WBS" full>
                  <Select value={extracted.wbs_id || "__none"} onValueChange={(v) => setExtracted({ ...extracted, wbs_id: v === "__none" ? "" : v })}>
                    <SelectTrigger data-testid="ocr-wbs"><SelectValue placeholder="— untagged —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— untagged —</SelectItem>
                      {wbsList.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Description" full><Textarea rows={2} value={extracted.description} onChange={(e) => setExtracted({...extracted, description: e.target.value})} /></Field>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="ocr-save" onClick={save} disabled={!extracted || busy} className="bg-primary hover:bg-primary/90">
            Save as expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkOCRDialog({ projectId, wbsList = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);   // [{name, size, type, dataUrl}]
  const [rows, setRows] = useState([]);     // [{status, filename, error?, fields{}}]
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultWbs, setDefaultWbs] = useState("");

  const reset = () => { setFiles([]); setRows([]); setProgress({ done: 0, total: 0 }); setBusy(false); setSaving(false); };

  const acceptFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter(
      (f) => /^image\/(jpeg|jpg|png|webp)$|^application\/pdf$/i.test(f.type)
    );
    if (!arr.length) return toast.error("Drop images or PDFs only");
    setFiles(arr);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const items = e.dataTransfer.items;
    if (items && items.length && items[0].webkitGetAsEntry) {
      const collected = [];
      const walk = (entry, cb) => {
        if (entry.isFile) entry.file((f) => { collected.push(f); cb(); });
        else if (entry.isDirectory) {
          const reader = entry.createReader();
          reader.readEntries((entries) => {
            let pending = entries.length;
            if (!pending) return cb();
            entries.forEach((ent) => walk(ent, () => { pending--; if (pending === 0) cb(); }));
          });
        } else cb();
      };
      let pending = items.length;
      const done = () => { pending--; if (pending === 0) acceptFiles(collected); };
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : null;
        if (entry) walk(entry, done);
        else done();
      }
    } else {
      acceptFiles(e.dataTransfer.files);
    }
  };

  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const runScan = async () => {
    if (!files.length) return;
    setBusy(true);
    setRows([]);
    setProgress({ done: 0, total: files.length });
    const drafts = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const dataUrl = await readAsBase64(f);
        const b64 = dataUrl.split(",")[1];
        const r = await api.post("/expenses/ocr", { image_base64: b64, mime_type: f.type });
        drafts.push({
          status: "ready", filename: f.name,
          fields: {
            date: r.data.date || new Date().toISOString().slice(0, 10),
            vendor: r.data.vendor || "",
            invoice_no: r.data.invoice_no || "",
            description: r.data.description || "",
            amount: Number(r.data.amount || 0),
            gst_pct: Number(r.data.gst_pct || 18),
            tds_pct: Number(r.data.tds_pct || 0),
            category: "Aluminium Profile (YKK)",
            payment_mode: "bank",
            bill_image_path: r.data.bill_image_path || "",
            bill_content_type: r.data.bill_content_type || "",
            wbs_id: defaultWbs || "",
          },
          duplicate: null,
        });
        // Duplicate check — very strict: vendor + invoice_no + amount
        try {
          const dup = await api.post("/expenses/check-duplicate", {
            vendor: r.data.vendor || "", invoice_no: r.data.invoice_no || "",
            amount: Number(r.data.amount || 0),
          });
          if (dup.data.is_duplicate) {
            drafts[drafts.length - 1].duplicate = dup.data.matches?.[0] || null;
          }
        } catch { /* ignore dup check errors */ }
      } catch (err) {
        drafts.push({ status: "error", filename: f.name, error: (err?.response?.data?.detail || "OCR failed") });
      }
      setProgress({ done: i + 1, total: files.length });
      setRows([...drafts]);
    }
    setBusy(false);
    const ok = drafts.filter((r) => r.status === "ready").length;
    const dups = drafts.filter((r) => r.duplicate).length;
    if (dups) toast.warning(`${dups} likely duplicate(s) flagged — review before saving`);
    toast.success(`Scanned ${ok}/${files.length} bills · review before saving`);
  };

  const updateRow = (i, k, v) => setRows(rows.map((r, j) => j === i ? { ...r, fields: { ...r.fields, [k]: v } } : r));
  const removeRow = (i) => setRows(rows.filter((_, j) => j !== i));

  const saveAll = async () => {
    const ready = rows.filter((r) => r.status === "ready" && !r.duplicate);
    const dupCount = rows.filter((r) => r.status === "ready" && r.duplicate).length;
    if (!ready.length) return toast.error(dupCount ? "All ready rows are marked duplicate — remove or force-save individually" : "Nothing to save");
    setSaving(true);
    let saved = 0;
    for (const r of ready) {
      try {
        await api.post("/expenses", {
          project_id: projectId, ...r.fields, amount: Number(r.fields.amount),
          gst_pct: Number(r.fields.gst_pct), tds_pct: Number(r.fields.tds_pct),
        });
        saved++;
      } catch { /* keep going */ }
    }
    setSaving(false);
    toast.success(`${saved} expenses saved${dupCount ? ` · ${dupCount} duplicates skipped` : ""}`);
    setOpen(false);
    reset();
    onCreated();
  };

  const forceSaveOne = async (i) => {
    const r = rows[i];
    if (!r || r.status !== "ready") return;
    try {
      await api.post("/expenses", {
        project_id: projectId, ...r.fields, amount: Number(r.fields.amount),
        gst_pct: Number(r.fields.gst_pct), tds_pct: Number(r.fields.tds_pct),
      });
      setRows(rows.map((row, j) => j === i ? { ...row, status: "saved" } : row));
      toast.success("Force-saved despite duplicate warning");
      onCreated();
    } catch { toast.error("Failed"); }
  };

  const readyCount = rows.filter((r) => r.status === "ready" && !r.duplicate).length;
  const dupCount = rows.filter((r) => r.status === "ready" && r.duplicate).length;
  const errorCount = rows.filter((r) => r.status === "error").length;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="bulk-ocr-btn" className="border-secondary/50">
          <Layers className="h-3.5 w-3.5 mr-1 text-secondary" /> Bulk scan
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-secondary" /> Bulk vendor bill scan
          </DialogTitle>
          <DialogDescription>
            Drag-and-drop a whole folder of vendor bills — images or PDFs. Each file is scanned by Claude Sonnet 4.5 in sequence.
            You review and edit every draft before saving them as expenses.
          </DialogDescription>
        </DialogHeader>

        {rows.length === 0 && (
          <div
            data-testid="bulk-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border-2 border-dashed border-primary/30 rounded-lg p-8 text-center bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer"
            onClick={() => document.getElementById("bulk-file-input")?.click()}
          >
            <Layers className="h-8 w-8 mx-auto text-secondary mb-2" />
            <div className="text-sm font-medium">Drop a folder or a selection of bills here</div>
            <div className="text-xs text-muted-foreground mt-1">JPG · PNG · WEBP · PDF — as many as you like</div>
            <input
              id="bulk-file-input"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => acceptFiles(e.target.files)}
              data-testid="bulk-file-input"
            />
            {files.length > 0 && (
              <div className="mt-4 text-xs">
                <Badge className="bg-secondary/20 text-secondary-foreground border-secondary/30 font-mono">
                  {files.length} files queued
                </Badge>
              </div>
            )}
          </div>
        )}

        {files.length > 0 && rows.length === 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground truncate flex-1 min-w-[200px]">
              {files.slice(0, 5).map((f) => f.name).join(", ")}{files.length > 5 ? ` … +${files.length - 5} more` : ""}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase font-mono text-muted-foreground">Tag to WBS</Label>
              <Select value={defaultWbs || "__none"} onValueChange={(v) => setDefaultWbs(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="bulk-default-wbs" className="h-8 w-56 text-xs"><SelectValue placeholder="— untagged —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— untagged —</SelectItem>
                  {wbsList.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={() => setFiles([])} data-testid="bulk-clear">Clear</Button>
              <Button size="sm" onClick={runScan} disabled={busy} data-testid="bulk-start" className="bg-primary hover:bg-primary/90">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ScanLine className="h-3.5 w-3.5 mr-1" />}
                Start bulk scan
              </Button>
            </div>
          </div>
        )}

        {(busy || rows.length > 0) && (
          <div className="mt-3 space-y-2 flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <Loader2 className={`h-3.5 w-3.5 ${busy ? "animate-spin" : "hidden"}`} />
                <span className="font-mono">
                  {progress.done}/{progress.total} scanned · {readyCount} ready · {dupCount > 0 && <span className="text-amber-700">{dupCount} duplicate ·</span>} {errorCount} errors
                </span>
              </div>
              {!busy && readyCount > 0 && (
                <Button size="sm" onClick={saveAll} disabled={saving} data-testid="bulk-save-all"
                  className="bg-primary hover:bg-primary/90">
                  {saving ? "Saving…" : `Save ${readyCount} expenses`}
                </Button>
              )}
            </div>
            <div className="h-1 bg-muted rounded overflow-hidden">
              <div className="h-full bg-primary transition-all"
                style={{ width: progress.total ? `${(progress.done / progress.total) * 100}%` : 0 }} />
            </div>
            <div className="overflow-auto flex-1 border rounded-md">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card z-10 border-b">
                  <tr className="text-left text-[10px] text-muted-foreground uppercase font-mono">
                    <th className="py-2 px-2 font-medium w-6"></th>
                    <th className="py-2 px-2 font-medium">File</th>
                    <th className="py-2 px-2 font-medium">Vendor</th>
                    <th className="py-2 px-2 font-medium">Invoice #</th>
                    <th className="py-2 px-2 font-medium">Date</th>
                    <th className="py-2 px-2 font-medium">Category</th>
                    <th className="py-2 px-2 font-medium text-right">Amount</th>
                    <th className="py-2 px-2 font-medium text-right">GST%</th>
                    <th className="py-2 px-2 font-medium text-right">TDS%</th>
                    <th className="py-2 px-2 font-medium w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b ${r.status === "error" ? "bg-red-50" : r.duplicate ? "bg-amber-50" : r.status === "saved" ? "bg-emerald-50" : ""}`} data-testid={`bulk-row-${i}`}>
                      <td className="py-1.5 px-2">
                        {r.status === "saved"
                          ? <Check className="h-3.5 w-3.5 text-emerald-700" />
                          : r.duplicate
                            ? <span title={`Duplicate of ${r.duplicate.invoice_no}`} className="text-amber-700 font-bold text-[10px]">DUP</span>
                            : r.status === "ready"
                              ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                              : <X className="h-3.5 w-3.5 text-destructive" />}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-[10px] truncate max-w-[160px]">
                        {r.filename}
                        {r.duplicate && (
                          <div className="text-amber-700 text-[9px] font-mono mt-0.5" data-testid={`bulk-dup-${i}`}>
                            Duplicate of {r.duplicate.invoice_no} ({dateStr(r.duplicate.date)})
                          </div>
                        )}
                      </td>
                      {r.status === "error" ? (
                        <td colSpan={7} className="py-1.5 px-2 text-destructive text-[11px]">{r.error}</td>
                      ) : (
                        <>
                          <td className="py-1 px-2"><Input value={r.fields.vendor} onChange={(e) => updateRow(i, "vendor", e.target.value)} className="h-7 text-xs" /></td>
                          <td className="py-1 px-2"><Input value={r.fields.invoice_no} onChange={(e) => updateRow(i, "invoice_no", e.target.value)} className="h-7 text-xs w-24" /></td>
                          <td className="py-1 px-2"><Input type="date" value={r.fields.date} onChange={(e) => updateRow(i, "date", e.target.value)} className="h-7 text-xs w-32" /></td>
                          <td className="py-1 px-2">
                            <Select value={r.fields.category} onValueChange={(v) => updateRow(i, "category", v)}>
                              <SelectTrigger className="h-7 text-xs w-40"><SelectValue /></SelectTrigger>
                              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="py-1 px-2"><Input type="number" value={r.fields.amount} onChange={(e) => updateRow(i, "amount", e.target.value)} className="h-7 text-xs w-24 text-right" /></td>
                          <td className="py-1 px-2"><Input type="number" value={r.fields.gst_pct} onChange={(e) => updateRow(i, "gst_pct", e.target.value)} className="h-7 text-xs w-16 text-right" /></td>
                          <td className="py-1 px-2"><Input type="number" value={r.fields.tds_pct} onChange={(e) => updateRow(i, "tds_pct", e.target.value)} className="h-7 text-xs w-16 text-right" /></td>
                        </>
                      )}
                      <td className="py-1.5 px-2">
                        {r.duplicate && r.status === "ready" && (
                          <Button size="sm" variant="ghost" onClick={() => forceSaveOne(i)} data-testid={`bulk-force-${i}`}
                            className="h-6 px-2 text-[10px] text-amber-700 hover:text-amber-900">Save anyway</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => removeRow(i)} className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive">
                          <X className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
